import React, { useState, useEffect } from 'react';
import Card from './Card';
import DropdownButton from './DropdownButton';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import SelectButton from './SelectButton';
import ToggleSwitch from './ToggleSwitch';
import Button from './Button';

// 일정 폼 데이터 인터페이스 (다이얼로그용)
interface ScheduleFormData {
  title: string;
  description: string;
  time: string;
  type: string;
  category: string;
  isActive: boolean;
  // 추가 필드들
  dateOption?: 'allDay' | 'custom' | 'period';
  startDate?: string;
  endDate?: string;
  period?: string;
  isGoal?: boolean;
  goal?: string;
  id?: string;
}

// ScheduleSettingDialog props 인터페이스
interface ScheduleSettingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate?: Date | null;
  onScheduleSaved: (schedule: ScheduleFormData) => void;
  onScheduleDeleted?: (scheduleId: string) => void;
  scale?: number;
  mode?: 'create' | 'edit';
  initialData?: ScheduleFormData;
}

const ScheduleSettingDialog: React.FC<ScheduleSettingDialogProps> = ({
  isOpen,
  onClose,
  selectedDate,
  onScheduleSaved,
  onScheduleDeleted,
  scale = 1,
  mode = 'create',
  initialData
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [time, setTime] = useState('');
  const [type, setType] = useState('학급'); // 학급 또는 교과
  const [category, setCategory] = useState('일정'); // 일정 카테고리
  const [isActive, setIsActive] = useState(true);
  const [isGoal, setIsGoal] = useState(true);
  const [dateOption, setDateOption] = useState<'allDay' | 'custom' | 'period'>('custom');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [period, setPeriod] = useState('');
  const [goal, setGoal] = useState('');
  const periodOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
  ];
  const goalOptions = [
    { value: '목표 포인트', label: '목표 포인트' },
    { value: '목표 시간', label: '목표 시간' },
    { value: '목표 문제', label: '목표 문제' },
    { value: '목표 책', label: '목표 책' },
  ];
  // 드롭다운 옵션들, 추가해서 쓰세요
  const typeOptions = [
    { value: '학급', label: '학급' },
    { value: '교과', label: '교과' }
  ];
  
  const categoryOptions = [
    { value: '일정', label: '일정' },
    { value: '과제', label: '과제' },
    { value: '시험', label: '시험' },
    { value: '행사', label: '행사' },
    { value: '실험', label: '실험' },
    { value: '발표', label: '발표' },
    { value: '대회', label: '대회' },
    { value: '기타', label: '기타' }
  ];

  // 다이얼로그가 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        // 수정 모드: 기존 데이터로 채움
        setTitle(initialData.title || '');
        setDescription(initialData.description || '');
        setTime(initialData.time || '');
        setType(initialData.type || '학급');
        setCategory(initialData.category || '일정');
        setIsActive(initialData.isActive ?? true);
        setDateOption(initialData.dateOption || 'custom');
        setStartDate(initialData.startDate || '');
        setEndDate(initialData.endDate || '');
        setPeriod(initialData.period || '');
        setIsGoal(initialData.isGoal ?? true);
        setGoal(initialData.goal || '');
      } else {
        // 추가 모드: 빈 폼
        setTitle('');
        setDescription('');
        setTime('');
        setType('학급');
        setCategory('일정');
        setIsActive(true);
        setDateOption('custom');
        
        // selectedDate가 있으면 해당 날짜로 자동 입력
        if (selectedDate) {
          const year = selectedDate.getFullYear();
          const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
          const day = String(selectedDate.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;
          setStartDate(dateString);
          setEndDate(dateString);
        } else {
        setStartDate('');
        setEndDate('');
        }
        
        setPeriod('');
        setIsGoal(true);
        setGoal('');
      }
    }
  }, [isOpen, mode, initialData, selectedDate]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    // 시간 정보 구성
    let timeInfo = '';
    if (dateOption === 'allDay') {
      timeInfo = startDate ? `하루 종일 (${startDate})` : '하루 종일';
    } else if (dateOption === 'custom') {
      if (startDate && endDate) {
        timeInfo = `${startDate} ~ ${endDate}`;
      } else if (startDate) {
        timeInfo = startDate;
      }
    } else if (dateOption === 'period') {
      timeInfo = period ? `${period}교시` : '교시 미선택';
    }

    const scheduleData: ScheduleFormData = {
      title: title.trim(),
      description: description.trim(),
      time: timeInfo,
      type,
      category,
      isActive,
      // 추가 필드들
      dateOption,
      startDate,
      endDate,
      period,
      isGoal,
      goal
    };

    // 수정 모드인 경우 ID 포함
    const scheduleWithId = mode === 'edit' && initialData?.id 
      ? { ...scheduleData, id: initialData.id }
      : scheduleData;

    onScheduleSaved(scheduleWithId);
  };

  const handleDelete = () => {
    if (mode === 'edit' && initialData?.id && onScheduleDeleted) {
      if (window.confirm('정말로 이 일정을 삭제하시겠습니까?')) {
        onScheduleDeleted(initialData.id);
      }
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setTime('');
    setType('학급');
    setCategory('일정');
    setIsActive(true);
    setIsGoal(true);
    setDateOption('custom');
    setStartDate('');
    setEndDate('');
    setPeriod('');
    setGoal('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
       <Card 
       width="450px" 
       height="733px" 
       padding="24px 28px 20px 28px" 
       gap="161px" 
       style={{
         transformOrigin: 'center center',
         transform: `scale(${scale})`,
         overflow: 'hidden'
       }}>
        {/* 헤더 및 내용 영역*/}
        <div style={{
          width: '100%',
          height: '483px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: '40px'
        }}>
          <div style={{
            width: '100%',
            height: '32px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <h2 style={{
              fontSize: '20px',
              lineHeight: '30px',
              fontWeight: '600',
              color: '#161616',
              margin: 0
            }}>
              {mode === 'create' ? '새 일정 추가하기' : '일정 수정하기'}
            </h2>
            <img 
              src="/images/icons/closeIcon.svg"
              alt="닫기"
              onClick={handleClose}
              style={{
                width: '32px',
                height: '32px',
                padding: '9.4px',
                cursor: 'pointer'
              }}
            />
          </div>
          <div style={{
            width: '100%',
            height: '411px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '24px'
          }}>
            {/* 유형 선택, 일정 제목 입력, 일정 내용 입력 창*/}
            <div style={{
              width: '100%',
              height: '144px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: '12px'
            }}>
              {/* 유형 및 카테고리 선택, 일정 제목 입력창*/}
              <div style={{
                width: '100%',
                height: '48px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px'
              }}>
                {/* 유형 선택 */}
                <DropdownButton 
                options={typeOptions}
                width="100px"
                height="48px"
                value={type} 
                onChange={setType} 
                placeholder="유형" 
                />
                {/* 카테고리 선택 */}
                <DropdownButton 
                options={categoryOptions}
                width="100px"
                height="48px"
                value={category} 
                onChange={setCategory} 
                placeholder="카테고리" 
                />
                <InputField
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e)}
                  placeholder="일정 제목을 입력해주세요"
                  width="186px"
                  height="48px"
                  required
                  />
              </div>
              {/* 일정 내용 입력창*/}
              <TextAreaField
                value={description}
                onChange={(e) => setDescription(e)}
                placeholder="일정 내용을 입력해주세요"
                width="100%"
                height="84px"
                required
              />
            </div>
            {/* 유형 및 날짜, 시간 선택 영역 */}
            <div style={{
              width: '100%',
              height: '85px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '100%',
                height: '48px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px'
              }}>
                <SelectButton
                  width="57px"
                  selected={dateOption === 'allDay'}
                  onClick={() => setDateOption('allDay')}
                >
                  하루 종일
                </SelectButton>
                <SelectButton
                  width="79px"
                  selected={dateOption === 'custom'}
                  onClick={() => setDateOption('custom')}
                >
                  날짜 직접 입력
                </SelectButton>
                <SelectButton
                  width="36px"
                  selected={dateOption === 'period'}
                  onClick={() => setDateOption('period')}
                >
                  교시
                </SelectButton>
              </div>
              {/* 날짜 입력 영역 - 고정 높이 */}
              <div style={{
                width: '100%',
                minHeight: '48px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '8px'
              }}>
                {dateOption === 'allDay' && (
                  <InputField
                    type='date'
                    placeholder='날짜 선택'
                    required
                    value={startDate}
                    onChange={(value) => setStartDate(value)}
                    width="100%"
                    height="48px"
                  />
                )}
                {dateOption === 'custom' && (
                  <>
                    <InputField
                      type='date'
                      placeholder='시작 날짜 선택'
                      required
                      value={startDate}
                      onChange={(value) => setStartDate(value)}
                      width="100%"
                      height="48px"
                    />
                    <InputField
                      type='date'
                      placeholder='종료 날짜 선택'
                      required
                      value={endDate}
                      onChange={(value) => setEndDate(value)}
                      width="100%"
                      height="48px"
                    />
                  </>
                )}
                {dateOption === 'period' && (
                  <DropdownButton
                    options={periodOptions}
                    width="100%"
                    height="48px"
                    value={period}
                    onChange={(value) => setPeriod(value)}
                    placeholder="교시"
                  />                  
                )}
              </div>
            </div>
            {/* 목표 설정 영역 */}
            <div style={{
              width: '100%',
              height: '85px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                width: '100%',
                height: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <h3 style={{
                  height: '24px',
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: '600',
                  color: '#161616',
                  margin: 0
                }}>
                  목표 설정
                </h3>
                <ToggleSwitch
                 checked={isGoal}
                 onChange={setIsGoal}
                />
              </div>
              {isGoal && (
                <div style={{
                  width: '100%',
                  height: '48px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <DropdownButton
                    options={goalOptions}
                    width="128px"
                    height="48px"
                    value={goal}
                    onChange={(value) => setGoal(value)}
                    placeholder="목표"
                  />
                  {goal === '목표 포인트' && (
                  <InputField
                    type="number"
                    value={goal}
                    onChange={(value) => setGoal(value)}
                    placeholder="0"
                    width="258px"
                    height="48px"
                  />
                  )}
                  {goal === '목표 시간' && (
                    <InputField
                      type="number"
                      value={goal}
                      onChange={(value) => setGoal(value)}
                      placeholder="0"
                      width="258px"
                      height="48px"
                    />
                  )}
                </div>
              )}
            </div>
            {/* 활성화 버튼 */}
            <div style={{
                width: '100%',
                height: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <h3 style={{
                  height: '24px',
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: '600',
                  color: '#161616',
                  margin: 0
                }}>
                  활성화
                </h3>
                <ToggleSwitch
                 checked={isActive}
                 onChange={setIsActive}
                />
              </div>
          </div> 
        </div>
        <div
        style={{
          width: '100%',
          height: '45px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px'
        }}>
          {mode === 'create' ? (
            <>
              <Button variant="cancel" size="medium"
                children="취소"
                onClick={handleClose}
                width="124px"
              />
              <Button size="medium" variant="submit"
                children="등록"
                onClick={() => handleSubmit()}
                width="262px"
              />
            </>
          ) : (
            <>
              <Button variant="cancel" size="medium"
                children="삭제"
                onClick={handleDelete}
                width="124px"
              />
              <Button size="medium" variant="submit"
                children="저장"
                onClick={() => handleSubmit()}
                width="262px"
              />
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ScheduleSettingDialog;